import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Phone, PhoneCall, Voicemail, Calendar, CalendarCheck, Clock, MapPin, Star,
  MessageSquare, Bell, ShoppingCart, Wrench, Users, UserPlus, TrendingUp,
  BarChart3, Search, Globe, Sparkles, ClipboardCheck, FileText, Bot, Zap,
  Repeat, CheckCircle, Smartphone, Gift, Code2, Lock, LifeBuoy, ArrowRight
} from 'lucide-react';
import { SOLUTIONS } from '../data/solutionsData';
import './IndustryPage.css';
import './SolutionPage.css';

const ICONS = {
  Phone, PhoneCall, Voicemail, Calendar, CalendarCheck, Clock, MapPin, Star,
  MessageSquare, Bell, ShoppingCart, Wrench, Users, UserPlus, TrendingUp,
  BarChart3, Search, Globe, Sparkles, ClipboardCheck, FileText, Bot, Zap,
  Repeat, CheckCircle, Smartphone, Gift, Code2, Lock, LifeBuoy
};

const EASE = [0.22, 0.61, 0.36, 1];

export default function SolutionPage() {
  const { slug } = useParams();
  const reduce = useReducedMotion();
  const data = SOLUTIONS.find((s) => s.slug === slug);

  if (!data) return <Navigate to="/solutions" replace />;

  const Icon = ICONS[data.icon] || Sparkles;
  const {
    hero, painsTitle, painsSub, pains, whatTitle, whatSub, capabilities,
    includedTitle, included, related, cta
  } = data;

  const rise = (d = 0) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, amount: 0.25 },
          transition: { duration: 0.6, delay: d, ease: EASE }
        };

  return (
    <main className="ind-page">
      {/* HERO */}
      <section className="ind-hero">
        <div className="ind-wrap">
          <motion.span className="ind-badge" {...rise(0)}>
            <span className="ind-badge-ic"><Icon size={15} strokeWidth={2.3} /></span>
            {hero.eyebrow}
          </motion.span>
          <motion.h1 className="ind-h1" {...rise(0.06)}>
            {hero.lead} <span className="ind-gold">{hero.accent}</span>.
          </motion.h1>
          <motion.p className="ind-sub" {...rise(0.12)}>{hero.sub}</motion.p>
          <motion.div className="ind-cta-row" {...rise(0.18)}>
            <Link to="/plans" className="ind-btn ind-btn-primary">
              Get started <ArrowRight size={17} strokeWidth={2.4} />
            </Link>
            <Link to="/book" className="ind-btn ind-btn-ghost">See Versa in action</Link>
          </motion.div>
          <motion.p className="ind-trust" {...rise(0.24)}>
            One of six pillars we run for you. <Link to="/solutions" className="sol-inline-link">See all solutions</Link>
          </motion.p>
        </div>
      </section>

      {/* PAINS */}
      <section className="ind-section ind-section-tint">
        <div className="ind-wrap">
          <motion.div className="ind-head" {...rise(0)}>
            <h2 className="ind-h2">{painsTitle}</h2>
            {painsSub && <p className="ind-head-sub">{painsSub}</p>}
          </motion.div>
          <div className="ind-pains-grid">
            {pains.map((p, i) => (
              <motion.div className="ind-pain" key={p.title} {...rise(i * 0.06)}>
                <span className="ind-pain-num">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="ind-pain-title">{p.title}</h3>
                <p className="ind-pain-text">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="ind-section">
        <div className="ind-wrap">
          <motion.div className="ind-head" {...rise(0)}>
            <span className="ind-eyebrow">{data.name}</span>
            <h2 className="ind-h2">{whatTitle}</h2>
            {whatSub && <p className="ind-head-sub">{whatSub}</p>}
          </motion.div>
          <div className="ind-sol-grid">
            {capabilities.map((c, i) => {
              const Ci = ICONS[c.icon] || Sparkles;
              return (
                <motion.div className="ind-sol" key={c.title} {...rise((i % 3) * 0.06)}>
                  <span className="ind-sol-ic"><Ci size={24} strokeWidth={2} /></span>
                  <h3 className="ind-sol-title">{c.title}</h3>
                  <p className="ind-sol-text">{c.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHAT IS INCLUDED */}
      <section className="ind-section ind-section-dark">
        <div className="ind-wrap">
          <motion.div className="ind-head" {...rise(0)}>
            <h2 className="ind-h2">{includedTitle}</h2>
          </motion.div>
          <ul className="sol-included">
            {included.map((item, i) => (
              <motion.li className="sol-included-item" key={item} {...rise((i % 3) * 0.05)}>
                <CheckCircle size={18} strokeWidth={2.4} aria-hidden="true" />
                <span>{item}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* RELATED */}
      {related && related.length > 0 && (
        <section className="ind-section">
          <div className="ind-wrap">
            <motion.div className="ind-head" {...rise(0)}>
              <h2 className="ind-h2">Where this connects</h2>
              <p className="ind-head-sub">
                Nothing here runs on its own. These are the pieces this pillar works with most.
              </p>
            </motion.div>
            <motion.div className="sol-related" {...rise(0.06)}>
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="sol-related-link">
                  {r.label} <ArrowRight size={15} strokeWidth={2.4} />
                </Link>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="ind-section ind-final">
        <div className="ind-wrap">
          <motion.h2 className="ind-final-title" {...rise(0)}>{cta.title}</motion.h2>
          <motion.p className="ind-final-text" {...rise(0.06)}>{cta.text}</motion.p>
          <motion.div className="ind-cta-row" {...rise(0.12)}>
            <Link to="/plans" className="ind-btn ind-btn-onfill">
              Get started <ArrowRight size={17} strokeWidth={2.4} />
            </Link>
            <Link to="/book" className="ind-btn ind-btn-onfill-ghost">See Versa in action</Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
